import { Component, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import Button from './Button'

/**
 * Global çökme koruması (019). Alt ağaçta beklenmeyen render hatasını yakalar; boş/beyaz ekran
 * yerine anlaşılır hata ekranı + toparlanma gösterir. React'te hata sınırı yalnız sınıf bileşeniyle
 * kurulur (ek paket yok). Ham hata detayı kullanıcıya sızdırılmaz (yalnız dev günlüğü).
 */
export interface ErrorBoundaryFallbackArgs {
  error: unknown
  reset: () => void
}

interface ErrorBoundaryProps {
  children: ReactNode
  /** Fallback özelleştirmesi; verilmezse varsayılan hata ekranı (i18n). */
  fallback?: (args: ErrorBoundaryFallbackArgs) => ReactNode
  /** İçinden biri değişince boundary sıfırlanır (ör. route pathname) — FR-013. */
  resetKeys?: unknown[]
  /** "Ana ekrana dön" ile çağrılır (ör. navigate('/')). */
  onReset?: () => void
}

interface ErrorBoundaryState {
  hasError: boolean
  error: unknown
}

/** Varsayılan hata ekranı — i18n; sınıf bileşeni hook kullanamadığından ayrı fonksiyon. */
function DefaultErrorFallback({ reset, onReset }: Readonly<{ reset: () => void; onReset?: () => void }>) {
  const { t } = useTranslation()
  const goHome = () => {
    reset()
    onReset?.()
  }
  return (
    <div className="error-boundary" role="alert">
      <h1 className="error-boundary__title">{t('common.errors.crashTitle')}</h1>
      <p className="error-boundary__body">{t('common.errors.crashBody')}</p>
      <div className="error-boundary__actions">
        <Button label={t('common.errors.retry')} onClick={reset} />
        {onReset && (
          <Button variant="secondary" label={t('common.errors.goHome')} onClick={goHome} />
        )}
      </div>
    </div>
  )
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: unknown): void {
    // Prod'da da konsola yazılır (yalnız DevTools açan biri görür — kullanıcıya gösterilen ekrana
    // sızmaz, FR-003 ihlal edilmez). Önceden yalnız DEV'de yazılıyordu; bir prod hatasının (rdc dev,
    // 2026-08-15) React'in kendi minified iz'inden başka hiçbir ipucu bırakmaması, teşhisi saatler
    // sürdürdü — konum (route) bilgisi en azından "hangi ekran" sorusunu hemen yanıtlar.
    console.error('ErrorBoundary yakaladı:', error, 'konum:', window.location.pathname)
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (!this.state.hasError) return
    const { resetKeys } = this.props
    const prevKeys = prevProps.resetKeys
    if (!resetKeys || !prevKeys) return
    const changed =
      resetKeys.length !== prevKeys.length || resetKeys.some((k, i) => !Object.is(k, prevKeys[i]))
    if (changed) this.reset()
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const args: ErrorBoundaryFallbackArgs = { error: this.state.error, reset: this.reset }
      if (this.props.fallback) return this.props.fallback(args)
      return <DefaultErrorFallback reset={this.reset} onReset={this.props.onReset} />
    }
    return this.props.children
  }
}
